import { Levels, LayerIndex } from '../zIndex';
import { grid } from '../grid';
import { font } from '../font';

import { Theme } from '../theme';
import { content } from '../content';
import misc from '../misc';
import transition from '../transition';
import { ColorType, transparentizeColor } from '../colors';

type ThemeFn = ({ theme }: { theme: Theme }) => string;

export interface ThemeGet {
  zIndex: (level: Levels, layer: LayerIndex) => ThemeFn;
  colors: (color: ColorType, transparency?: number) => ThemeFn;
  fonts: (fontname: keyof typeof font['fonts']) => ThemeFn;
  /**
   * @function fontSize
   * @param size size index
   * @description
   * index: size in rem
   * 0: '1rem',
   * 1: '1.1rem',
   * 2: '1.2rem',
   * 3: '1.3rem',
   * 4: '1.4rem',
   * 5: '1.5rem',
   * 6: '1.6rem',
   * 7: '1.8rem',
   * 8: '2rem',
   * 9: '2.1rem',
   * 10: '2.2rem',
   * 11: '2.3rem',
   * 12: '2.4rem',
   * 13: '2.5rem',
   * 14: '2.6rem',
   * 15: '2.8rem',
   * 16: '3.2rem',
   *
   */
  fontSize: (size: number) => ThemeFn;
  fontWeight: (weight: keyof typeof font['fontWeights']) => ThemeFn;
  /**
   * @function space
   * @param space index
   * @param unit unit, defaults to 'px'
   * @description
   * index: space
   *  0: 0, 1: 4, 2: 8, 3: 16, 4: 24, 5: 32, 6: 40, 7: 64.
   */
  space: (space: number, unit?: string) => ThemeFn;
  content: (name: keyof typeof content) => ThemeFn;
  misc: (name: keyof typeof misc) => ThemeFn;
  transition: (property?: string, speed?: keyof typeof transition) => ThemeFn;
}

export const themeGet: ThemeGet = {
  zIndex:
    (level, layer) =>
    ({ theme }) =>
      `${theme.zIndex[level][layer]}`,
  colors:
    (color, transparency) =>
    ({ theme }) => {
      const themedColor = theme.colors[color];
      return transparency === undefined ? themedColor : transparentizeColor(themedColor, transparency);
    },
  fonts:
    (font) =>
    ({ theme }) =>
      theme.fonts[font],
  fontWeight:
    (weight) =>
    ({ theme }) =>
      theme.fontWeights[weight],
  fontSize: (size) => {
    if (size > font.fontSizes.length - 1) {
      throw Error(`font size can not be higher than ${font.fontSizes.length - 1}`);
    }
    return ({ theme }) => theme.fontSizes[size];
  },
  space: (space, unit = 'px') => {
    if (space > grid.space.length - 1) {
      throw Error(`space can not be higher than ${grid.space.length - 1}`);
    }
    return ({ theme }) => `${theme.space[space]}${unit}`;
  },
  content:
    (name) =>
    ({ theme }) =>
      theme.content[name] as string,
  misc:
    (name) =>
    ({ theme }) =>
      theme.misc[name],
  transition:
    (property = 'all', speed = 'normal') =>
    ({ theme }) =>
      `${property} ${theme.transition[speed]}`,
};
